import { forwardRef, type MouseEventHandler } from 'react';
import { useSelector } from 'react-redux';

import { useRouter } from '~/utils/navigation';
import Icon, { ICON_SEARCH_LINE, ICON_CIRCLE_CLOSE, ICON_HEART_FILLED } from '~/components/icon/index';
import useCasinoRoutes from '~/components/router/use-casino-routes';
import {
	MobileWrapper,
	MobileInputWithIconsWrapper,
	SearchInputField,
	SearchResultsCancelButton
} from './common-search.styles';
import { selectEnableLiveCasino3, selectGlobalNavigationEnabled } from '~/components/brand/ducks/selectors';
import useCasinoLocation from '~/hooks/use-casino-location';

const classNames = (classes: string[]) => {
	return classes
		.map(className => className.trim())
		.filter(className => !!className)
		.join(' ');
};

type Props = {
	inSearchRoute: boolean;
	keyword: string;
	updateSearchKeyword: (keyword: string) => void;
	clear: () => void;
	navigateToSearch: MouseEventHandler<HTMLButtonElement>;
	placeholder: string;
	cancel: () => void;
	cancelText: string;
	blackFridayCampaignEnabled: boolean;
	disabled?: boolean;
};
const SearchMobile = forwardRef<HTMLInputElement, Props>(
	(
		{
			inSearchRoute,
			keyword,
			updateSearchKeyword,
			placeholder,
			clear,
			cancel,
			cancelText,
			navigateToSearch,
			blackFridayCampaignEnabled,
			disabled
		},
		ref
	) => {
		const router = useRouter();
		const globalNavigationEnabled = useSelector(selectGlobalNavigationEnabled);
		const enableLiveCasino3 = useSelector(selectEnableLiveCasino3);
		const { isMyGamesLocation, isLiveCasinoLocation } = useCasinoLocation();
		const { ROUTE_CASINO_MY_GAMES_LOVED, ROUTE_LIVE_CASINO_MY_GAMES_LOVED } = useCasinoRoutes();
		return (
			<MobileWrapper
				inSearchRoute={inSearchRoute}
				className={classNames([
					'mobile-search-container',
					blackFridayCampaignEnabled ? 'mobile-search-container--black-friday' : ''
				])}
			>
				{!globalNavigationEnabled || inSearchRoute ? (
					<button
						className={`search-icon ${inSearchRoute ? 'search-icon-active' : ''}`}
						data-at="search-icon-casino-nav-bar"
						onClick={navigateToSearch}
					>
						<Icon type={ICON_SEARCH_LINE} />
					</button>
				) : null}

				{!globalNavigationEnabled && enableLiveCasino3 && !inSearchRoute && (
					<button
						className={`search-icon favorite-games-icon ${
							isMyGamesLocation ? 'favorite-games-icon--active' : ''
						}`}
						onClick={() =>
							router.push(
								isLiveCasinoLocation ? ROUTE_LIVE_CASINO_MY_GAMES_LOVED : ROUTE_CASINO_MY_GAMES_LOVED
							)
						}
					>
						<Icon type={ICON_HEART_FILLED} />
					</button>
				)}

				{inSearchRoute && (
					<>
						<MobileInputWithIconsWrapper className="mobile-search-wrapper">
							<SearchInputField
								id="search-bar"
								ref={ref}
								onChange={event => updateSearchKeyword(event.target.value)}
								placeholder={placeholder}
								value={inSearchRoute ? keyword : ''}
								className={`${inSearchRoute ? 'search-input-active' : ''}`}
								type="search"
								data-at="search-inputfield-casino-nav-bar"
								bg={
									blackFridayCampaignEnabled
										? 'rgba(var(--base-07), 1)'
										: 'var(--search-bg, rgba(var(--base-11), 1))'
								}
								disabled={disabled}
							/>
							{keyword && inSearchRoute && (
								<button
									className="search-icon search-icon--right search-icon-active"
									onClick={clear}
									data-at="search-close-icon-casino-nav-bar"
								>
									<Icon type={ICON_CIRCLE_CLOSE} />
								</button>
							)}
						</MobileInputWithIconsWrapper>

						<SearchResultsCancelButton onClick={cancel} data-at="search-cancel-button-casino-nav-bar">
							{cancelText}
						</SearchResultsCancelButton>
					</>
				)}
			</MobileWrapper>
		);
	}
);
SearchMobile.displayName = 'SearchMobile';

export default SearchMobile;
